import imageUrlBuilder from '@sanity/image-url';
import { client } from './client';
import type { SanityImageSource } from '@sanity/image-url/lib/types/types';
import createImageUrlBuilder from '@sanity/image-url';

const builder = imageUrlBuilder(client);
const hotspotBuilder = createImageUrlBuilder(client);

type CompatibleImageBuilder = {
  width: (value: number) => CompatibleImageBuilder;
  height: (value: number) => CompatibleImageBuilder;
  quality: (value: number) => CompatibleImageBuilder;
  fit: (value: string) => CompatibleImageBuilder;
  auto: (value: string) => CompatibleImageBuilder;
  dpr: (value: number) => CompatibleImageBuilder;
  url: () => string;
};

type BynderAsset = {
  _type?: string;
  id?: string | null;
  url?: string | null;
  selectedUrl?: string | null;
  width?: number | null;
  height?: number | null;
  aspectRatio?: number | null;
};

const COMMON_IMAGE_KEYS = ['image', 'thumbnail', 'poster', 'icon', 'src'] as const;
const SANITY_IMAGE_REF_PATTERN = /^image-[A-Za-z0-9]+-\d+x\d+-[A-Za-z0-9]+$/;

const isRecord = (value: unknown): value is Record<string, any> => typeof value === 'object' && value !== null;

const isRemoteUrl = (value: unknown): value is string => typeof value === 'string' && /^(https?:)?\/\//i.test(value);

const hasValidSanityAssetRef = (value: unknown): value is { asset: { _ref: string } } =>
  isRecord(value) && typeof value.asset?._ref === 'string' && SANITY_IMAGE_REF_PATTERN.test(value.asset._ref);

const isBynderTransformUrl = (value: string) => {
  try {
    const url = new URL(value);
    return /\.bynder\.com$/i.test(url.hostname) && url.pathname.includes('/transform/') && url.searchParams.has('io');
  } catch {
    return false;
  }
};

const updateBynderTransformDimension = (url: URL, key: 'width' | 'height', value: string) => {
  const io = url.searchParams.get('io');
  if (!io?.startsWith('transform:')) return false;

  const prefix = 'transform:';
  const parts = io
    .slice(prefix.length)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  let updated = false;
  const nextParts = parts.map((part) => {
    if (part.startsWith(`${key}:`)) {
      updated = true;
      return `${key}:${value}`;
    }
    return part;
  });

  if (!updated) {
    nextParts.push(`${key}:${value}`);
  }

  url.searchParams.set('io', `${prefix}${nextParts.join(',')}`);
  return true;
};

const getBynderAsset = (value: unknown): BynderAsset | null => {
  if (!isRecord(value)) return null;

  if (isRecord(value.bynderImage)) {
    return value.bynderImage as BynderAsset;
  }

  if (value._type === 'bynder.asset' || 'selectedUrl' in value || 'databaseId' in value) {
    return value as BynderAsset;
  }

  return null;
};

const unwrapImageSource = (value: unknown): any => {
  if (!isRecord(value)) return value;
  if (getBynderAsset(value) || value.asset || value.url) return value;

  for (const key of COMMON_IMAGE_KEYS) {
    if (value[key]) return unwrapImageSource(value[key]);
  }

  return value;
};

const getDirectImageUrl = (value: unknown): string | null => {
  const source = unwrapImageSource(value);
  if (isRemoteUrl(source)) return source;
  if (!isRecord(source)) return null;

  const bynderAsset = getBynderAsset(source);
  if (bynderAsset) {
    return bynderAsset.selectedUrl ?? bynderAsset.url ?? null;
  }

  if (isRemoteUrl(source.url)) return source.url;
  if (isRecord(source.asset) && isRemoteUrl(source.asset.url)) return source.asset.url;

  return null;
};

const wrapRawUrlBuilder = (baseUrl: string, seedParams?: URLSearchParams): CompatibleImageBuilder => {
  const params = new URLSearchParams(seedParams);

  const api: CompatibleImageBuilder = {
    width(value) {
      params.set('width', String(value));
      return api;
    },
    height(value) {
      params.set('height', String(value));
      return api;
    },
    quality(value) {
      params.set('quality', String(value));
      return api;
    },
    fit(value) {
      params.set('fit', value);
      return api;
    },
    auto(value) {
      params.set('auto', value);
      return api;
    },
    dpr(value) {
      params.set('dpr', String(value));
      return api;
    },
    url() {
      try {
        const url = new URL(baseUrl);
        const isBynderTransform = isBynderTransformUrl(url.toString());
        params.forEach((value, key) => {
          if (isBynderTransform && (key === 'width' || key === 'height')) {
            if (updateBynderTransformDimension(url, key, value)) {
              return;
            }
          }

          if (isBynderTransform && key === 'focus' && !url.searchParams.has('focuspoint')) {
            url.searchParams.set('focuspoint', value);
            return;
          }

          if (isBynderTransform && (key === 'fit' || key === 'auto' || key === 'quality' || key === 'dpr')) {
            return;
          }

          url.searchParams.set(key, value);
        });
        return url.toString();
      } catch {
        return baseUrl;
      }
    },
  };

  return api;
};

const wrapSanityBuilder = (initialBuilder: any, fallbackSource?: unknown): CompatibleImageBuilder => {
  let current = initialBuilder;

  const api: CompatibleImageBuilder = {
    width(value) {
      current = current.width(value);
      return api;
    },
    height(value) {
      current = current.height(value);
      return api;
    },
    quality(value) {
      current = current.quality(value);
      return api;
    },
    fit(value) {
      current = current.fit(value);
      return api;
    },
    auto(value) {
      current = current.auto(value);
      return api;
    },
    dpr(value) {
      current = current.dpr(value);
      return api;
    },
    url() {
      try {
        return current.url();
      } catch {
        return getDirectImageUrl(fallbackSource) ?? '';
      }
    },
  };

  return api;
};

export const hasImageSource = (value: unknown) => {
  const source = unwrapImageSource(value);
  if (!source) return false;

  if (isRemoteUrl(source)) return true;
  if (!isRecord(source)) return false;

  return Boolean(hasValidSanityAssetRef(source) || source.asset?.url || source.url || getBynderAsset(source));
};

export function urlFor(source: SanityImageSource) {
  const resolved = unwrapImageSource(source);
  const bynderAsset = getBynderAsset(resolved);

  if (bynderAsset) {
    const directUrl = getDirectImageUrl(bynderAsset);
    const baseUrl = directUrl ?? '';
    const seedParams = new URLSearchParams();

    if (directUrl) {
      try {
        const parsed = new URL(directUrl);
        const focuspoint = parsed.searchParams.get('focuspoint');
        if (focuspoint) seedParams.set('focuspoint', focuspoint);
      } catch {
        // Ignore malformed URLs and fall back to the base URL.
      }
    }

    return wrapRawUrlBuilder(baseUrl, seedParams);
  }

  if (isRemoteUrl(resolved)) {
    return wrapRawUrlBuilder(resolved);
  }

  if (isRecord(resolved) && isRemoteUrl(resolved.asset?.url || resolved.url)) {
    return wrapRawUrlBuilder(resolved.asset?.url || resolved.url);
  }

  if (hasValidSanityAssetRef(resolved)) {
    return wrapSanityBuilder(builder.image(resolved), resolved);
  }

  return wrapRawUrlBuilder('');
}

// Pick one aspect for the hero
export const HERO_ASPECT = { w: 16, h: 9 }; // change to {21,9} for panorama
export const GALLERY_ASPECT = { w: 1, h: 1 };

// Next/Image loader: receives width from the browser, returns URL with matching height
export const sanityHeroLoader =
  (img: any, aspect = HERO_ASPECT) =>
  ({ width, quality }: { src: string; width: number; quality?: number }) => {
    const h = Math.round((width * aspect.h) / aspect.w);
    const resolved = unwrapImageSource(img);
    const bynderAsset = getBynderAsset(resolved);

    if (!bynderAsset && hasValidSanityAssetRef(resolved)) {
      return wrapSanityBuilder(
        hotspotBuilder
          .image(resolved)
          .width(width)
          .height(h)
          .fit('crop')
          .auto('format')
          .quality(quality ?? 75),
        resolved,
      ).url();
    }

    return urlFor(resolved).width(width).height(h).url();
  };

export const sanityImageSrc = (img: any, opts?: { width?: number | null; height?: number | null }) => {
  if (!img) return '';

  const { width, height } = opts || {};
  const resolved = unwrapImageSource(img);
  const bynderAsset = getBynderAsset(resolved);

  let compatibleBuilder: CompatibleImageBuilder;
  if (!bynderAsset && hasValidSanityAssetRef(resolved)) {
    compatibleBuilder = wrapSanityBuilder(hotspotBuilder.image(resolved).fit('crop').auto('format'), resolved);
  } else {
    compatibleBuilder = urlFor(resolved);
  }

  if (typeof width === 'number') {
    compatibleBuilder = compatibleBuilder.width(width);
  }

  if (typeof height === 'number') {
    compatibleBuilder = compatibleBuilder.height(height);
  }

  return compatibleBuilder.url();
};
